import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";
import { SocketContext } from "../Socket/SocketContext";

// Settings da empresa carregadas uma vez por sessão. Antes cada TicketsListCustom
// montado refazia o GET /settings só para saber do `ticketConnectionScope`, e o
// NotificationsPopOver não tinha acesso nenhum a ele (tocava som de ticket que a
// lista escondia).
const SettingsContext = createContext({
  settings: [],
  getSetting: () => null,
  connectionScopeEnabled: false,
  notifyGroupMessages: false,
});

const SettingsProvider = ({ children }) => {
  const { user, isAuth } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const [settings, setSettings] = useState([]);

  const companyId = user?.companyId;

  useEffect(() => {
    if (!isAuth || !companyId) {
      setSettings([]);
      return undefined;
    }

    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!active) return;
        setSettings(Array.isArray(data) ? data : []);
      } catch (err) {
        // Silencioso: na falha mantém [] => escopo desligado (comportamento atual)
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuth, companyId]);

  useEffect(() => {
    if (!isAuth || !companyId) return undefined;

    const socket = socketManager.getSocket(companyId);
    const handleSettings = (data) => {
      const setting = data?.setting;
      if (!setting?.key) return;
      setSettings((prev) => {
        const rest = prev.filter((s) => s.key !== setting.key);
        return [...rest, setting];
      });
    };

    socket.on(`company-${companyId}-settings`, handleSettings);
    return () => {
      socket.off(`company-${companyId}-settings`, handleSettings);
    };
  }, [isAuth, companyId, socketManager]);

  const getSetting = useCallback(
    (key) => settings.find((s) => s.key === key)?.value ?? null,
    [settings]
  );

  const value = useMemo(
    () => ({
      settings,
      getSetting,
      connectionScopeEnabled: getSetting("ticketConnectionScope") === "enabled",
      notifyGroupMessages: getSetting("notifyGroupMessages") === "enabled",
    }),
    [settings, getSetting]
  );

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
};

export { SettingsContext, SettingsProvider };
